import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
	GitBranch,
	Loader2,
	CheckCircle2,
	ExternalLink,
	AlertTriangle,
	Webhook,
	FolderGit2,
	Unlink,
	Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SectionBlock } from "./SectionBlock";
import { GithubSetupModal, type GithubSetupVariant } from "./GithubSetupModal";
import {
	getGithubStatus,
	connectGithub,
	startGithubAppCreation,
	registerGithubInstallation,
	disconnectGithubInstallation,
	removeGithubApp,
	listProjectRepos,
	listGithubRepos,
	type GithubStatus,
	type GithubInstallation,
	type ProjectRepoWithProject,
} from "@/services/github.service";

// GitHub's per-installation repo-access settings page. Works for personal
// accounts and orgs alike (GitHub redirects into the right context).
function installationConfigureUrl(installationId: number) {
	return `https://github.com/settings/installations/${installationId}`;
}

// Where an admin sets up the GitHub App when the server has no credentials yet.
const SETUP_DOCS_URL = "https://github.com/settings/apps/new";

const REDIRECT_ERRORS: Record<string, string> = {
	cancelled: "The GitHub flow was cancelled.",
	state: "The session expired or the request could not be verified. Try again.",
	manifest: "GitHub App creation failed. Try again.",
	exchange: "Saving the connection failed. Try again.",
};

function AppDetails({ status }: { status: GithubStatus }) {
	const app = status.app;
	if (!app) return null;
	return (
		<div className="rounded-lg border border-border divide-y divide-border text-sm">
			<div className="flex items-center justify-between px-3 py-2">
				<span className="text-muted">GitHub App</span>
				{app.html_url ? (
					<a
						href={app.html_url}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-1 font-mono text-primary hover:underline"
					>
						{app.slug ?? `app #${app.app_id}`}
						<ExternalLink className="h-3 w-3" />
					</a>
				) : (
					<span className="font-mono text-foreground">
						{app.slug ?? `app #${app.app_id}`}
					</span>
				)}
			</div>
			<div className="flex items-center justify-between px-3 py-2">
				<span className="text-muted">App ID</span>
				<span className="font-mono text-foreground">{app.app_id}</span>
			</div>
			<div className="flex items-center justify-between px-3 py-2">
				<span className="text-muted">Webhook</span>
				<span
					className={`inline-flex items-center gap-1.5 text-xs font-medium ${
						app.webhook_configured
							? "text-secondary"
							: "text-warning"
					}`}
				>
					<Webhook className="h-3.5 w-3.5" />
					{app.webhook_configured
						? "Active (live PR updates)"
						: "Not configured (manual refresh)"}
				</span>
			</div>
		</div>
	);
}

function InstalledAccounts({
	installations,
	repoCounts,
	disconnectingId,
	onDisconnect,
}: {
	installations: GithubInstallation[];
	repoCounts: Record<number, number>;
	disconnectingId: number | null;
	onDisconnect: (installationId: number, accountLabel: string) => void;
}) {
	if (installations.length === 0) {
		return (
			<p className="text-xs text-muted italic">
				Not installed on any account yet.
			</p>
		);
	}
	return (
		<div className="rounded-lg border border-border divide-y divide-border text-sm">
			{installations.map((i) => {
				const label = i.account_login ?? `#${i.installation_id}`;
				const isBusy = disconnectingId === i.installation_id;
				const count = repoCounts[i.installation_id] ?? 0;
				return (
					<div
						key={i.installation_id}
						className="flex items-center justify-between px-3 py-2 gap-3"
					>
						<span className="font-mono text-foreground truncate">
							{label}
							{i.account_type && (
								<span className="text-muted opacity-60">
									{" "}
									· {i.account_type}
								</span>
							)}
						</span>
						<div className="flex items-center gap-3 shrink-0">
							<span className="text-xs text-muted">
								{count} {count === 1 ? "repo" : "repos"} granted
							</span>
							<a
								href={installationConfigureUrl(i.installation_id)}
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
							>
								Configure
								<ExternalLink className="h-3 w-3" />
							</a>
							<Button
								variant="ghost"
								size="sm"
								disabled={isBusy}
								onClick={() =>
									onDisconnect(i.installation_id, label)
								}
								className="h-7 px-2 text-xs text-muted hover:text-destructive"
							>
								{isBusy ? (
									<Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
								) : (
									<Unlink className="h-3.5 w-3.5 mr-1" />
								)}
								Disconnect
							</Button>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function ConnectedRepos({ repos }: { repos: ProjectRepoWithProject[] }) {
	if (repos.length === 0) {
		return (
			<p className="text-xs text-muted italic">
				No projects have a repository linked yet.
			</p>
		);
	}
	return (
		<div className="rounded-lg border border-border divide-y divide-border text-sm">
			{repos.map((repo) => (
				<div
					key={repo.project_id}
					className="flex items-center justify-between px-3 py-2 gap-3"
				>
					<span className="inline-flex items-center gap-1.5 text-foreground min-w-0">
						<FolderGit2 className="h-3.5 w-3.5 text-muted shrink-0" />
						{repo.project ? (
							<Link
								to={`/projects/${repo.project.id}`}
								className="hover:underline truncate"
							>
								{repo.project.name}
							</Link>
						) : (
							<span className="text-muted italic">
								Unknown project
							</span>
						)}
					</span>
					<span className="font-mono text-xs text-muted-foreground truncate">
						{repo.repo_owner}/{repo.repo_name}
						<span className="opacity-60">
							{" "}
							· {repo.default_branch}
						</span>
					</span>
				</div>
			))}
		</div>
	);
}

export function GithubIntegrationSection() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [status, setStatus] = useState<GithubStatus | null>(null);
	const [loading, setLoading] = useState(true);
	const [connecting, setConnecting] = useState(false);
	const [creating, setCreating] = useState(false);
	const [modalVariant, setModalVariant] = useState<GithubSetupVariant | null>(
		null,
	);
	const [projectRepos, setProjectRepos] = useState<ProjectRepoWithProject[]>(
		[],
	);
	const [repoCounts, setRepoCounts] = useState<Record<number, number>>({});
	const [disconnectingId, setDisconnectingId] = useState<number | null>(null);
	const [removingApp, setRemovingApp] = useState(false);

	const loadStatus = useCallback(async () => {
		try {
			const s = await getGithubStatus();
			setStatus(s);
			if (s.configured && s.connected) {
				listProjectRepos()
					.then((repos) => {
						// PostgREST may return the embedded project as an object or a
						// single-element array depending on relationship detection.
						setProjectRepos(
							repos.map((r) => ({
								...r,
								project: (Array.isArray(r.project)
									? (r.project[0] ?? null)
									: r.project) as {
									id: string;
									name: string;
								} | null,
							})),
						);
					})
					.catch(() => setProjectRepos([]));

				listGithubRepos()
					.then((repos) => {
						const counts: Record<number, number> = {};
						for (const r of repos) {
							counts[r.installation_id] =
								(counts[r.installation_id] ?? 0) + 1;
						}
						setRepoCounts(counts);
					})
					.catch(() => setRepoCounts({}));
			}
		} catch {
			setStatus({ configured: false, connected: false });
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		// loadStatus only setStates after an await, not synchronously.
		// eslint-disable-next-line react-hooks/set-state-in-effect
		loadStatus();
	}, [loadStatus]);

	// Surface the GitHub redirect result (app created / installed / error),
	// then strip the params. Same pattern as the gcal handler in TasksPage.
	useEffect(() => {
		const result = searchParams.get("github");
		if (!result) return;

		if (result === "app_created") {
			toast.success("GitHub App created", {
				description:
					"Credentials captured. Install it on your repos next.",
			});
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setModalVariant("app_created");
		} else if (result === "connected") {
			loadStatus();
			setModalVariant("connected");
		} else if (result === "installed") {
			// GitHub sent the install redirect to the frontend (Apps created
			// before the setup URL pointed at the backend). Register it now.
			const installationId = Number(searchParams.get("installation_id"));
			if (Number.isInteger(installationId) && installationId > 0) {
				registerGithubInstallation(installationId)
					.then(() => {
						loadStatus();
						setModalVariant("connected");
					})
					.catch((e) => {
						toast.error(
							"Could not register the GitHub installation",
							{
								description:
									e instanceof Error ? e.message : undefined,
							},
						);
					});
			} else {
				loadStatus();
				setModalVariant("connected");
			}
		} else if (result === "error") {
			const reason = searchParams.get("reason") ?? "";
			toast.error("GitHub setup failed", {
				description: REDIRECT_ERRORS[reason] ?? "Please try again.",
			});
		}

		const next = new URLSearchParams(searchParams);
		for (const key of [
			"github",
			"reason",
			"installation_id",
			"setup_action",
			"state",
		]) {
			next.delete(key);
		}
		setSearchParams(next, { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	async function handleConnect() {
		setConnecting(true);
		try {
			const url = await connectGithub();
			window.location.href = url;
		} catch (e) {
			toast.error(
				e instanceof Error ? e.message : "GitHub is not configured.",
			);
			setConnecting(false);
		}
	}

	async function handleDisconnect(
		installationId: number,
		accountLabel: string,
	) {
		const confirmed = window.confirm(
			`Disconnect "${accountLabel}"? Projects linked to a repo on this account will lose their repository mapping. Branch/PR history on tasks is kept.`,
		);
		if (!confirmed) return;

		setDisconnectingId(installationId);
		try {
			await disconnectGithubInstallation(installationId);
			toast.success(`Disconnected ${accountLabel}.`);
			await loadStatus();
		} catch (e) {
			toast.error(
				e instanceof Error
					? e.message
					: "Could not disconnect the account.",
			);
		} finally {
			setDisconnectingId(null);
		}
	}

	async function handleRemoveApp() {
		const confirmed = window.confirm(
			"Remove this GitHub App from TaskHub? This deletes the stored credentials and all installations here, so every project's repo mapping is cleared. Branch/PR history on tasks is kept. This does not delete the App on GitHub itself — delete it there too if you want the name freed up.",
		);
		if (!confirmed) return;

		setRemovingApp(true);
		try {
			await removeGithubApp();
			toast.success("GitHub App removed. You can create a new one now.");
			await loadStatus();
		} catch (e) {
			toast.error(
				e instanceof Error
					? e.message
					: "Could not remove the GitHub App.",
			);
		} finally {
			setRemovingApp(false);
		}
	}

	async function handleCreateApp() {
		setCreating(true);
		try {
			await startGithubAppCreation();
			// navigates to GitHub on success
		} catch (e) {
			toast.error(
				e instanceof Error
					? e.message
					: "Could not start GitHub App setup.",
			);
			setCreating(false);
		}
	}

	return (
		<SectionBlock
			title="GitHub Integration"
			description="Connect a GitHub account so tasks can create branches and track pull request status."
		>
			<GithubSetupModal
				variant={modalVariant}
				webhookConfigured={status?.app?.webhook_configured ?? false}
				installing={connecting}
				onInstall={handleConnect}
				onClose={() => setModalVariant(null)}
			/>

			{loading ? (
				<div className="flex items-center gap-2 text-sm text-muted">
					<Loader2 className="h-4 w-4 animate-spin" />
					Loading...
				</div>
			) : !status?.configured ? (
				// Server has no GitHub App credentials yet.
				<div className="flex flex-col gap-3 max-w-xl">
					<div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 p-3">
						<AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
						<div className="text-sm text-foreground">
							<p className="font-medium">
								GitHub integration is not configured.
							</p>
							<p className="text-xs text-muted mt-0.5">
								Create a GitHub App for this workspace. The form
								on GitHub is pre-filled with this system's
								settings; once you click Create, credentials are
								captured automatically.
							</p>
						</div>
					</div>
					<Button
						onClick={handleCreateApp}
						disabled={creating}
						className="self-start"
					>
						{creating ? (
							<Loader2 className="h-4 w-4 animate-spin mr-2" />
						) : (
							<GitBranch className="h-4 w-4 mr-2" />
						)}
						Create GitHub App
					</Button>
					<a
						href={SETUP_DOCS_URL}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-1.5 text-xs text-muted hover:text-primary hover:underline self-start"
					>
						Or set it up manually
						<ExternalLink className="h-3 w-3" />
					</a>
				</div>
			) : status.connected ? (
				// Configured and an installation exists.
				<div className="flex flex-col gap-3 max-w-xl">
					<div className="flex items-center gap-2 rounded-lg border border-border bg-muted-subtle/40 p-3">
						<CheckCircle2 className="h-4 w-4 text-secondary shrink-0" />
						<p className="text-sm font-medium text-foreground">
							Connected
						</p>
					</div>
					<AppDetails status={status} />

					<div>
						<p className="text-xs font-medium text-foreground mb-1.5">
							Installed accounts
						</p>
						<InstalledAccounts
							installations={status.installations ?? []}
							repoCounts={repoCounts}
							disconnectingId={disconnectingId}
							onDisconnect={handleDisconnect}
						/>
					</div>

					<div>
						<p className="text-xs font-medium text-foreground mb-1.5">
							Connected repositories
						</p>
						<ConnectedRepos repos={projectRepos} />
					</div>

					<Button
						variant="outline"
						size="sm"
						onClick={handleConnect}
						disabled={connecting}
						className="self-start"
					>
						{connecting && (
							<Loader2 className="h-4 w-4 animate-spin mr-2" />
						)}
						<GitBranch className="h-4 w-4 mr-2" />
						Add another account
					</Button>
					<p className="text-xs text-muted">
						Map a repository to each project in its Settings tab to
						enable branch creation and PR tracking.
					</p>
					<RemoveAppButton
						removing={removingApp}
						onRemove={handleRemoveApp}
					/>
				</div>
			) : (
				// Configured (App exists) but not installed on any account yet.
				<div className="flex flex-col gap-3 max-w-xl">
					<AppDetails status={status} />
					<p className="text-sm text-muted">
						The App is created but not installed yet. Install it on
						your GitHub account to pick which repositories it can
						access.
					</p>
					<Button
						onClick={handleConnect}
						disabled={connecting}
						className="self-start"
					>
						{connecting && (
							<Loader2 className="h-4 w-4 animate-spin mr-2" />
						)}
						<GitBranch className="h-4 w-4 mr-2" />
						Connect GitHub
					</Button>
					<RemoveAppButton
						removing={removingApp}
						onRemove={handleRemoveApp}
					/>
				</div>
			)}
		</SectionBlock>
	);
}

function RemoveAppButton({
	removing,
	onRemove,
}: {
	removing: boolean;
	onRemove: () => void;
}) {
	return (
		<div className="pt-2 border-t border-border">
			<Button
				variant="ghost"
				size="sm"
				onClick={onRemove}
				disabled={removing}
				className="self-start text-xs text-danger! hover:text-destructive"
			>
				{removing ? (
					<Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
				) : (
					<Trash2 className="h-3.5 w-3.5 mr-1.5" />
				)}
				Remove GitHub App
			</Button>
			<p className="text-xs text-muted mt-1">
				Clears the stored App and installations here so you can create a
				new one. Delete the App on GitHub separately if you also want
				its name freed up.
			</p>
		</div>
	);
}
